import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  DollarSign,
  Download,
  ListChecks,
  Loader2,
  Package,
  Send,
  Truck,
  X,
} from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { DropdownField, FormField } from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  calcNetProfit,
  calcProfitMargin,
  useApp,
  type Sale,
} from "@/context/AppContext";
import {
  fetchPickList,
  initPickList,
  type ApiPickRow,
} from "@/context/catalogSync";
import { apiFetch } from "@/lib/apiClient";

export const Route = createFileRoute("/sales/$id/")({
  component: SaleDetailPage,
});

const CARRIERS = [
  { value: "USPS", label: "USPS" },
  { value: "FEDEX", label: "FedEx" },
  { value: "UPS", label: "UPS" },
  { value: "DHL", label: "DHL" },
  { value: "OTHER", label: "Other" },
];

type Status =
  | "Sold"
  | "Picked"
  | "Packed"
  | "Shipped"
  | "Cancelled"
  | "Refunded";

function resolveStatus(sale: Sale): Status {
  // Display precedence (per the sales schema doc): Cancelled > Refunded >
  // Shipped > Packed > Picked > Sold.
  if (sale.cancelledAt) return "Cancelled";
  if (sale.refundedAt) return "Refunded";
  if (sale.shipped) return "Shipped";
  if (sale.packed) return "Packed";
  if (sale.pickedAt) return "Picked";
  return "Sold";
}

// Render an after-commit eBay action outcome (cancel TNK-159 / refund TNK-175)
// as a small chip. "ok" / "skipped" / "not-connected" are stable labels;
// "failed: <msg>" carries the truncated error from the api so the operator can
// act without grep'ing pino. `okLabel` differs per action (cancelled/refunded).
function EbayOutcomeBadge({
  status,
  okLabel,
}: {
  status: string;
  okLabel: string;
}) {
  const isFailure = status === "not-connected" || status.startsWith("failed:");
  const isOk = status === "ok";
  const isSkipped = status === "skipped";

  let label: string;
  if (isOk) label = okLabel;
  else if (status === "not-connected") label = "eBay: not connected";
  else if (isSkipped) label = "eBay: skipped (non-eBay sale)";
  else if (status.startsWith("failed:"))
    label = `eBay: ${status.slice("failed:".length).trim()}`;
  else label = `eBay: ${status}`;

  const className = isOk
    ? "inline-block rounded border border-emerald-700/40 bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800"
    : isFailure
      ? "inline-block rounded border border-red-700/40 bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800"
      : "inline-block rounded border border-muted-foreground/30 bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground";
  return <span className={className}>{label}</span>;
}

const EBAY_CANCEL_REASONS = [
  {
    value: "OUT_OF_STOCK_OR_CANNOT_FULFILL",
    label: "Can't fulfill / out of stock",
  },
  { value: "ADDRESS_ISSUES", label: "Address issues" },
  { value: "BUYER_ASKED_CANCEL", label: "Buyer asked to cancel" },
  { value: "BUYER_DID_NOT_PAY", label: "Buyer didn't pay" },
  { value: "WONT_ARRIVE_ON_TIME", label: "Won't arrive on time" },
] as const;

function SaleDetailPage() {
  const { id } = Route.useParams();
  const { sales, products, updateSale, cancelSale, refundSale } = useApp();
  const sale = useMemo(() => sales.find((s) => s.id === id), [sales, id]);
  // TNK-102: surface "(archived)" next to the product link if the
  // underlying Product has been soft-deleted. The Sale itself stays intact
  // because `itemId` is a soft pointer.
  const linkedProductArchived = useMemo(() => {
    if (!sale || sale.itemType !== "Product") return false;
    return products.some((p) => p.id === sale.itemId && !!p.archivedAt);
  }, [products, sale]);

  const [buyerName, setBuyerName] = useState(sale?.buyerName ?? "");
  const [buyerAddress, setBuyerAddress] = useState(sale?.buyerAddress ?? "");
  const [trackingNumber, setTrackingNumber] = useState(
    sale?.trackingNumber ?? "",
  );
  const [carrierCode, setCarrierCode] = useState(sale?.carrierCode ?? "");
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [shipTracking, setShipTracking] = useState(sale?.trackingNumber ?? "");
  const [shipCarrier, setShipCarrier] = useState(sale?.carrierCode || "USPS");
  const [shipPushToEbay, setShipPushToEbay] = useState(true);
  const [shipSubmitting, setShipSubmitting] = useState(false);
  // Cancel-sale dialog state (TNK-111). Restock defaults true (the common
  // case — buyer backed out, cards go back to inventory). The eBay-reason
  // dropdown only renders when the sale's venue is eBay.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelRestock, setCancelRestock] = useState(true);
  const [cancelEbayReason, setCancelEbayReason] = useState<
    (typeof EBAY_CANCEL_REASONS)[number]["value"]
  >("OUT_OF_STOCK_OR_CANNOT_FULFILL");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  // Refund-buyer dialog state (TNK-175). Amount is a dollars string,
  // pre-filled with the full paid total and editable for partial refunds.
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundDialogReason, setRefundDialogReason] = useState("");
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [actionBanner, setActionBanner] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const [fetchingBuyer, setFetchingBuyer] = useState(false);

  const status = sale ? resolveStatus(sale) : "Sold";

  // Pick-list state (TNK-102). For Product sales, load on mount; non-Product
  // sales have no pick list. The number of pending/completed rows drives the
  // Pick panel and the Mark Packed gate.
  const [pickRows, setPickRows] = useState<ApiPickRow[] | null>(null);
  const [pickLoading, setPickLoading] = useState(false);
  const [pickInitializing, setPickInitializing] = useState(false);
  useEffect(() => {
    if (!sale || sale.itemType !== "Product") {
      setPickRows(null);
      return;
    }
    let cancelled = false;
    setPickLoading(true);
    fetchPickList(sale.id)
      .then((rows) => {
        if (!cancelled) setPickRows(rows);
      })
      .catch(() => {
        if (!cancelled) setPickRows(null);
      })
      .finally(() => {
        if (!cancelled) setPickLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sale]);

  const pickStats = useMemo(() => {
    if (!pickRows) return null;
    const total = pickRows.length;
    const picked = pickRows.filter((r) => r.pickedCardId).length;
    return { total, picked };
  }, [pickRows]);

  // Mark Packed gate: when a pick list exists AND isn't complete, block
  // Mark Packed so the operator can't skip the pick step. Pre-existing
  // sales (no pick list initialized) and non-Product sales fall through.
  const pickGateBlocks = !!pickStats && pickStats.total > 0 && !sale?.pickedAt;

  const handleStartPick = async () => {
    if (!sale) return;
    setPickInitializing(true);
    try {
      const rows = await initPickList(sale.id);
      setPickRows(rows);
      setActionBanner({
        kind: "success",
        text: rows.length === 0 ? "No cards to pick." : "Pick list ready.",
      });
    } catch (err) {
      setActionBanner({
        kind: "error",
        text:
          err instanceof Error
            ? `Couldn't start pick: ${err.message}`
            : "Couldn't start pick.",
      });
    } finally {
      setPickInitializing(false);
    }
  };

  const persistSale = useCallback(
    (patch: Partial<Sale>) => {
      if (!sale) return;
      updateSale({ ...sale, ...patch });
    },
    [sale, updateSale],
  );

  const handleMarkPacked = () => {
    if (!sale) return;
    if (pickGateBlocks) {
      setActionBanner({
        kind: "error",
        text: "Finish the pick list before marking packed.",
      });
      return;
    }
    persistSale({ packed: true, packedAt: new Date().toISOString() });
    setActionBanner({ kind: "success", text: "Marked Packed." });
  };

  const handleOpenCancelDialog = () => {
    if (!sale) return;
    setCancelReason("");
    setCancelRestock(true);
    setCancelEbayReason("OUT_OF_STOCK_OR_CANNOT_FULFILL");
    setCancelDialogOpen(true);
  };

  const handleSubmitCancel = async () => {
    if (!sale) return;
    if (!cancelReason.trim()) {
      setActionBanner({ kind: "error", text: "Reason is required." });
      return;
    }
    setCancelSubmitting(true);
    setActionBanner(null);
    const result = await cancelSale(sale.id, {
      reason: cancelReason.trim(),
      restock: cancelRestock,
      ebayCancelReason: sale.venue === "eBay" ? cancelEbayReason : undefined,
    });
    setCancelSubmitting(false);
    if (result.error) {
      setActionBanner({ kind: "error", text: result.error });
      return;
    }
    setCancelDialogOpen(false);
    setActionBanner({
      kind: "success",
      text: cancelRestock
        ? "Sale cancelled. Cards returned to inventory, supplies re-credited."
        : "Sale cancelled.",
    });
  };

  const handleOpenRefundDialog = () => {
    if (!sale) return;
    // Default to the full amount the buyer paid (item price + their shipping).
    setRefundAmount((sale.salePrice + sale.buyerShipping).toFixed(2));
    setRefundDialogReason("");
    setRefundDialogOpen(true);
  };

  const handleSubmitRefund = async () => {
    if (!sale) return;
    const amountCents = Math.round(parseFloat(refundAmount) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setActionBanner({
        kind: "error",
        text: "Enter a refund amount greater than 0.",
      });
      return;
    }
    if (!refundDialogReason.trim()) {
      setActionBanner({ kind: "error", text: "Reason is required." });
      return;
    }
    setRefundSubmitting(true);
    setActionBanner(null);
    const result = await refundSale(sale.id, {
      amountCents,
      reason: refundDialogReason.trim(),
    });
    setRefundSubmitting(false);
    if (result.error) {
      setActionBanner({ kind: "error", text: result.error });
      return;
    }
    setRefundDialogOpen(false);
    setActionBanner({
      kind: "success",
      text:
        sale.venue === "eBay" && sale.ebayOrderId
          ? "Refund recorded. eBay refund submitted — refresh for the outcome."
          : "Refund recorded.",
    });
  };

  const handleOpenShipDialog = () => {
    if (!sale) return;
    setShipTracking(trackingNumber);
    setShipCarrier(carrierCode || "USPS");
    setShipPushToEbay(
      sale.venue === "eBay" && !!sale.ebayOrderId && !sale.ebayFulfillmentId,
    );
    setShipDialogOpen(true);
  };

  const handleSubmitShipped = async () => {
    if (!sale) return;
    if (!shipTracking.trim()) {
      setActionBanner({ kind: "error", text: "Tracking number is required." });
      return;
    }
    setShipSubmitting(true);
    setActionBanner(null);
    const now = new Date().toISOString();
    const patch: Partial<Sale> = {
      trackingNumber: shipTracking.trim(),
      carrierCode: shipCarrier,
      shipped: true,
      shippedAt: now,
      packed: sale.packed ?? true,
      packedAt: sale.packedAt ?? sale.shippedAt ?? now,
    };
    let pushedFulfillmentId: string | undefined;
    let pushedError: string | null = null;
    if (shipPushToEbay && sale.ebayOrderId) {
      try {
        const res = await apiFetch(
          `/api/ebay/orders/${encodeURIComponent(sale.ebayOrderId)}/ship`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              trackingNumber: shipTracking.trim(),
              carrierCode: shipCarrier,
            }),
          },
        );
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          pushedError = `eBay push failed (${res.status})${text ? `: ${text.slice(0, 200)}` : ""}`;
        } else {
          const json = (await res.json()) as { fulfillmentId?: string };
          pushedFulfillmentId = json.fulfillmentId;
        }
      } catch (e) {
        pushedError =
          e instanceof Error ? e.message : "eBay push failed unexpectedly";
      }
    }
    if (pushedFulfillmentId) patch.ebayFulfillmentId = pushedFulfillmentId;
    persistSale(patch);
    setTrackingNumber(shipTracking.trim());
    setCarrierCode(shipCarrier);
    setShipDialogOpen(false);
    setShipSubmitting(false);
    if (pushedError) {
      setActionBanner({
        kind: "error",
        text: `Marked Shipped locally, but ${pushedError}. Tracking saved; re-attempt the eBay push later if needed.`,
      });
    } else {
      setActionBanner({
        kind: "success",
        text: shipPushToEbay
          ? "Marked Shipped + pushed tracking to eBay."
          : "Marked Shipped.",
      });
    }
  };

  const handleRefetchBuyer = async () => {
    if (!sale) return;
    if (sale.venue !== "eBay" || !sale.itemId) return;
    setFetchingBuyer(true);
    setActionBanner(null);
    try {
      const res = await apiFetch(
        `/api/ebay/orders/by-listing/${encodeURIComponent(sale.itemId)}`,
      );
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        setActionBanner({
          kind: "error",
          text: `Could not fetch buyer info from eBay (${res.status})${text ? `: ${text.slice(0, 200)}` : ""}`,
        });
        return;
      }
      const json = (await res.json()) as {
        orderId?: string;
        buyerName?: string;
        buyerAddress?: string;
      };
      const patch: Partial<Sale> = {};
      if (json.buyerName) {
        patch.buyerName = json.buyerName;
        setBuyerName(json.buyerName);
      }
      if (json.buyerAddress) {
        patch.buyerAddress = json.buyerAddress;
        setBuyerAddress(json.buyerAddress);
      }
      if (json.orderId && !sale.ebayOrderId) patch.ebayOrderId = json.orderId;
      if (Object.keys(patch).length > 0) persistSale(patch);
      setActionBanner({ kind: "success", text: "Buyer info refreshed." });
    } catch (e) {
      setActionBanner({
        kind: "error",
        text: e instanceof Error ? e.message : "Buyer fetch failed",
      });
    } finally {
      setFetchingBuyer(false);
    }
  };

  if (!sale) {
    return (
      <section className="flex h-full flex-col items-center justify-center gap-3 py-12 text-center">
        <p className="text-sm text-muted-foreground">Sale not found</p>
        <BackButton fallback={{ to: "/sales" }} variant="wide" />
      </section>
    );
  }

  const net = calcNetProfit(sale);
  const margin = calcProfitMargin(sale);
  const supplyTotal =
    sale.suppliesBreakdown?.reduce((sum, s) => sum + s.unitCost, 0) ?? 0;

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <BackButton fallback={{ to: "/sales" }} variant="wide" />
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto py-4">
        {/* Title row */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
              {sale.id}
            </div>
            <h1 className="text-xl font-bold text-foreground">
              {sale.itemName}
              {linkedProductArchived && (
                <span className="ml-2 align-middle rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                  archived
                </span>
              )}
            </h1>
            <div className="text-xs text-muted-foreground">
              {sale.saleDate} · {sale.venue}
            </div>
          </div>
          <StatusBadge status={status} />
        </div>

        {/* Action row */}
        <div className="flex flex-wrap gap-2">
          {sale.itemType === "Product" &&
            !sale.shipped &&
            !sale.packed &&
            !sale.pickedAt &&
            (pickRows === null || pickRows.length === 0) && (
              <PrimaryButton
                label={pickInitializing ? "Starting…" : "Start Pick"}
                leadingIcon={<ListChecks className="h-4 w-4" />}
                onClick={() => void handleStartPick()}
                disabled={pickInitializing}
                variant="secondary"
              />
            )}
          {pickRows && pickRows.length > 0 && !sale.pickedAt && (
            <Link
              to="/sales/$id/pick"
              params={{ id: sale.id }}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-secondary px-3 text-sm font-medium text-secondary-foreground hover:opacity-90"
            >
              <ListChecks className="h-4 w-4" />
              Continue Pick ({pickStats?.picked ?? 0}/{pickStats?.total ?? 0})
            </Link>
          )}
          {!sale.packed && !sale.shipped && (
            <PrimaryButton
              label="Mark Packed"
              leadingIcon={<Package className="h-4 w-4" />}
              onClick={handleMarkPacked}
              disabled={pickGateBlocks}
            />
          )}
          {!sale.shipped && (
            <PrimaryButton
              label="Mark Shipped"
              leadingIcon={<Truck className="h-4 w-4" />}
              onClick={handleOpenShipDialog}
            />
          )}
          {sale.shipped && sale.ebayOrderId && !sale.ebayFulfillmentId && (
            <PrimaryButton
              label="Push tracking to eBay"
              leadingIcon={<Send className="h-4 w-4" />}
              onClick={handleOpenShipDialog}
              variant="secondary"
            />
          )}
          {/* Cancel sale (TNK-111). Allowed up through Packed; once Shipped,
              the package is in the buyer's hands and refund is a different
              flow. Hidden when already cancelled. */}
          {!sale.cancelledAt && !sale.shipped && (
            <button
              type="button"
              onClick={handleOpenCancelDialog}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-3 text-sm font-medium text-destructive hover:bg-destructive/20"
            >
              <Ban className="h-4 w-4" />
              Cancel sale
            </button>
          )}
          {/* Refund buyer (TNK-175). Independent of cancel — available on any
              sale not yet refunded (e.g. a shipped order, or a partial). */}
          {!sale.refundedAt && (
            <button
              type="button"
              onClick={handleOpenRefundDialog}
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground hover:bg-muted"
            >
              <DollarSign className="h-4 w-4" />
              Refund buyer
            </button>
          )}
        </div>

        {sale.cancelledAt && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold">
                Cancelled {new Date(sale.cancelledAt).toLocaleString()}
              </div>
              {sale.cancelReason && (
                <div className="mt-0.5 text-destructive/80">
                  Reason: {sale.cancelReason}
                </div>
              )}
              {sale.refundedAt && (
                <div className="mt-0.5 text-destructive/80">
                  eBay refund submitted{" "}
                  {new Date(sale.refundedAt).toLocaleString()}
                </div>
              )}
              {sale.ebayCancelStatus && (
                <div className="mt-1">
                  <EbayOutcomeBadge
                    status={sale.ebayCancelStatus}
                    okLabel="eBay: cancelled"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {actionBanner && (
          <div
            className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
              actionBanner.kind === "success"
                ? "border-emerald-600 bg-emerald-100 text-emerald-700"
                : "border-destructive bg-destructive/10 text-destructive"
            }`}
          >
            {actionBanner.kind === "success" ? (
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            ) : (
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            )}
            <div className="flex-1">{actionBanner.text}</div>
            <button
              type="button"
              onClick={() => setActionBanner(null)}
              aria-label="Dismiss"
              className="text-inherit hover:opacity-70"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Financials */}
        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Financials
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Stat label="Sale Price" value={`$${sale.salePrice.toFixed(2)}`} />
            <Stat
              label="Net Profit"
              value={`$${net.toFixed(2)}`}
              tone={net >= 0 ? "positive" : "negative"}
            />
            <Stat
              label="Platform Fees"
              value={`$${sale.platformFees.toFixed(2)}`}
              tone="negative"
            />
            <Stat
              label="Payment Fees"
              value={`$${sale.paymentFees.toFixed(2)}`}
              tone="negative"
            />
            <Stat
              label="Buyer Paid Shipping"
              value={`$${sale.buyerShipping.toFixed(2)}`}
            />
            <Stat
              label="Actual Shipping"
              value={`$${sale.actualShipping.toFixed(2)}`}
              tone="negative"
            />
            <Stat
              label="Packaging Cost"
              value={`$${sale.packagingCost.toFixed(2)}`}
              tone="negative"
            />
            <Stat label="Margin" value={`${margin.toFixed(1)}%`} />
          </div>
        </div>

        {/* Pack list */}
        {sale.suppliesBreakdown && sale.suppliesBreakdown.length > 0 && (
          <div className="rounded-md border border-border bg-card p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Pack List
              </div>
              <div className="text-xs text-muted-foreground">
                Total: ${supplyTotal.toFixed(2)}
              </div>
            </div>
            <ul className="space-y-1.5">
              {sale.suppliesBreakdown.map((s) => (
                <li key={s.id} className="flex items-baseline gap-2 text-xs">
                  <span className="font-mono text-foreground">
                    {s.consumedQty && s.consumedQty > 0
                      ? `×${s.consumedQty}`
                      : "·"}
                  </span>
                  <span className="flex-1 text-foreground">{s.name}</span>
                  <span className="text-muted-foreground">
                    ${s.unitCost.toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Buyer info */}
        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Buyer
            </div>
            {sale.venue === "eBay" && sale.itemId && (
              <button
                type="button"
                onClick={handleRefetchBuyer}
                disabled={fetchingBuyer}
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-60"
              >
                {fetchingBuyer ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Download className="h-3 w-3" />
                )}
                Re-fetch from eBay
              </button>
            )}
          </div>
          <FormField
            label="Name"
            value={buyerName}
            onChangeText={setBuyerName}
            onBlur={() => persistSale({ buyerName: buyerName || undefined })}
            placeholder="Buyer name"
          />
          <div className="mb-4 flex flex-col gap-1.5">
            <label className="text-xs font-medium tracking-wide text-muted-foreground">
              Address
            </label>
            <textarea
              value={buyerAddress}
              onChange={(e) => setBuyerAddress(e.target.value)}
              onBlur={() =>
                persistSale({ buyerAddress: buyerAddress || undefined })
              }
              placeholder="Street, city, state, ZIP"
              rows={3}
              className="rounded-md border border-border bg-card px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        {/* Tracking */}
        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Shipping
          </div>
          <FormField
            label="Tracking Number"
            value={trackingNumber}
            onChangeText={setTrackingNumber}
            onBlur={() => persistSale({ trackingNumber: trackingNumber || "" })}
            placeholder="e.g. 9405536298093123456789"
          />
          <DropdownField
            label="Carrier"
            value={carrierCode || "USPS"}
            options={CARRIERS}
            onChange={(v) => {
              setCarrierCode(v);
              persistSale({ carrierCode: v });
            }}
          />
          {sale.ebayFulfillmentId && (
            <div className="rounded-md bg-muted px-3 py-2 text-[11px] text-muted-foreground">
              Pushed to eBay · fulfillment id{" "}
              <span className="font-mono">{sale.ebayFulfillmentId}</span>
            </div>
          )}
        </div>

        {/* Lifecycle timeline (TNK-175) — chronological audit incl. cancel +
            refund outcomes. Only rows with a timestamp render. */}
        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Timeline
          </div>
          <ul className="space-y-1.5 text-[11px] text-muted-foreground">
            <TimelineRow label="Created" at={sale.createdAt} />
            {sale.pickedAt && <TimelineRow label="Picked" at={sale.pickedAt} />}
            {sale.packedAt && <TimelineRow label="Packed" at={sale.packedAt} />}
            {sale.shippedAt && (
              <TimelineRow label="Shipped" at={sale.shippedAt} />
            )}
            {sale.cancelledAt && (
              <TimelineRow
                label="Cancelled"
                at={sale.cancelledAt}
                detail={sale.cancelReason}
                badge={
                  sale.ebayCancelStatus ? (
                    <EbayOutcomeBadge
                      status={sale.ebayCancelStatus}
                      okLabel="eBay: cancelled"
                    />
                  ) : null
                }
              />
            )}
            {sale.refundedAt && (
              <TimelineRow
                label="Refunded"
                at={sale.refundedAt}
                detail={
                  [
                    sale.refundCents != null
                      ? `$${(sale.refundCents / 100).toFixed(2)}`
                      : null,
                    sale.refundReason,
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
                badge={
                  sale.ebayRefundStatus ? (
                    <EbayOutcomeBadge
                      status={sale.ebayRefundStatus}
                      okLabel="eBay: refunded"
                    />
                  ) : null
                }
              />
            )}
          </ul>
        </div>
      </div>

      {/* Mark Shipped dialog */}
      {shipDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-background/80 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="flex w-full max-w-md flex-col rounded-md border border-border bg-card shadow-lg">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="text-base font-semibold text-foreground">
                Mark Shipped
              </div>
              <button
                type="button"
                onClick={() => !shipSubmitting && setShipDialogOpen(false)}
                aria-label="Close"
                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2 px-4 py-3">
              <FormField
                label="Tracking Number"
                value={shipTracking}
                onChangeText={setShipTracking}
                placeholder="e.g. 9405536298093123456789"
                required
              />
              <DropdownField
                label="Carrier"
                value={shipCarrier}
                options={CARRIERS}
                onChange={setShipCarrier}
              />
              {sale.venue === "eBay" && sale.ebayOrderId && (
                <label className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={shipPushToEbay}
                    onChange={(e) => setShipPushToEbay(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-primary"
                  />
                  <span>
                    Push tracking to eBay (Fulfillment API) — eBay marks the
                    order shipped and the buyer sees the tracking.
                  </span>
                </label>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
              <button
                type="button"
                onClick={() => setShipDialogOpen(false)}
                disabled={shipSubmitting}
                className="rounded-md bg-muted px-3 py-1.5 text-sm font-semibold text-muted-foreground hover:bg-muted/70 disabled:opacity-60"
              >
                Cancel
              </button>
              <PrimaryButton
                label={shipSubmitting ? "Submitting…" : "Mark Shipped"}
                onClick={handleSubmitShipped}
                loading={shipSubmitting}
              />
            </div>
          </div>
        </div>
      )}

      {/* Cancel sale dialog (TNK-111) */}
      {cancelDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-background/80 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="flex w-full max-w-md flex-col rounded-md border border-border bg-card shadow-lg">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="text-base font-semibold text-foreground">
                Cancel sale
              </div>
              <button
                type="button"
                onClick={() => !cancelSubmitting && setCancelDialogOpen(false)}
                aria-label="Close"
                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 px-4 py-3">
              <FormField
                label="Reason"
                value={cancelReason}
                onChangeText={setCancelReason}
                placeholder="e.g. card arrived damaged, buyer asked to cancel…"
                multiline
                required
              />
              <label className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={cancelRestock}
                  onChange={(e) => setCancelRestock(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-primary"
                  disabled={!!sale.pickedAt && cancelRestock === false}
                />
                <span>
                  Return cards to inventory + re-credit supplies + reopen the
                  linked listing. Uncheck only when the cards are genuinely gone
                  (damaged, lost).
                  {sale.pickedAt && (
                    <span className="mt-1 block font-semibold text-amber-700">
                      Required after the pick step.
                    </span>
                  )}
                </span>
              </label>
              {sale.venue === "eBay" && sale.ebayOrderId && (
                <DropdownField
                  label="eBay cancel reason"
                  value={cancelEbayReason}
                  options={EBAY_CANCEL_REASONS.map((r) => ({
                    value: r.value,
                    label: r.label,
                  }))}
                  onChange={(v) =>
                    setCancelEbayReason(
                      v as (typeof EBAY_CANCEL_REASONS)[number]["value"],
                    )
                  }
                />
              )}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
              <button
                type="button"
                onClick={() => setCancelDialogOpen(false)}
                disabled={cancelSubmitting}
                className="rounded-md bg-muted px-3 py-1.5 text-sm font-semibold text-muted-foreground hover:bg-muted/70 disabled:opacity-60"
              >
                Keep sale
              </button>
              <button
                type="button"
                onClick={() => void handleSubmitCancel()}
                disabled={cancelSubmitting || !cancelReason.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-1.5 text-sm font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-60"
              >
                <Ban className="h-4 w-4" />
                {cancelSubmitting ? "Cancelling…" : "Cancel sale"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund buyer dialog (TNK-175) */}
      {refundDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-background/80 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="flex w-full max-w-md flex-col rounded-md border border-border bg-card shadow-lg">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="text-base font-semibold text-foreground">
                Refund buyer
              </div>
              <button
                type="button"
                onClick={() => !refundSubmitting && setRefundDialogOpen(false)}
                aria-label="Close"
                className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 px-4 py-3">
              <FormField
                label="Refund amount"
                value={refundAmount}
                onChangeText={setRefundAmount}
                inputMode="decimal"
                suffix="USD"
                placeholder="0.00"
                required
              />
              <FormField
                label="Reason"
                value={refundDialogReason}
                onChangeText={setRefundDialogReason}
                placeholder="e.g. partial refund for a bent corner, buyer return…"
                multiline
                required
              />
              <p className="rounded-md bg-muted px-3 py-2 text-[11px] text-muted-foreground">
                {sale.venue === "eBay" && sale.ebayOrderId
                  ? "Issues the refund through eBay (full or partial). The outcome lands asynchronously — refresh to see it."
                  : "Records the refund locally (non-eBay sale — money handled outside eBay)."}
              </p>
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
              <button
                type="button"
                onClick={() => setRefundDialogOpen(false)}
                disabled={refundSubmitting}
                className="rounded-md bg-muted px-3 py-1.5 text-sm font-semibold text-muted-foreground hover:bg-muted/70 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSubmitRefund()}
                disabled={
                  refundSubmitting ||
                  !refundDialogReason.trim() ||
                  !refundAmount.trim()
                }
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
              >
                <DollarSign className="h-4 w-4" />
                {refundSubmitting ? "Refunding…" : "Refund buyer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function StatusBadge({ status }: { status: Status }) {
  const colors =
    status === "Cancelled"
      ? "border-destructive/40 bg-destructive/10 text-destructive"
      : status === "Refunded"
        ? "border-purple-600/40 bg-purple-100 text-purple-700"
        : status === "Shipped"
          ? "border-emerald-600/40 bg-emerald-100 text-emerald-700"
          : status === "Packed"
            ? "border-sky-600/40 bg-sky-100 text-sky-700"
            : "border-amber-600/40 bg-amber-100 text-amber-700";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${colors}`}
    >
      {status}
    </span>
  );
}

function TimelineRow({
  label,
  at,
  detail,
  badge,
}: {
  label: string;
  at: string;
  detail?: string;
  badge?: ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="min-w-[72px] font-medium text-foreground">{label}</span>
      <span>{new Date(at).toLocaleString()}</span>
      {detail && <span className="text-muted-foreground/80">— {detail}</span>}
      {badge}
    </li>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative";
}) {
  const valueClass =
    tone === "positive"
      ? "text-emerald-600"
      : tone === "negative"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div>
      <div className={`text-sm font-semibold ${valueClass}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
